import React, { useState, useEffect } from 'react';
import { listIssues, createIssue, getIssueComments, addIssueComment, closeIssue, reopenIssue } from 'service/ncoreBoardService';
import { useAuth } from 'contexts/AuthContext';
import styles from './IssuesTab.module.scss';

const TYPE_LABELS = {
    bug: { label: 'Баг', color: '#f87171' },
    enhancement: { label: 'Улучшение', color: '#4ade80' },
    question: { label: 'Вопрос', color: '#60a5fa' },
    documentation: { label: 'Документация', color: '#fbbf24' },
    other: { label: 'Другое', color: '#9ca3af' },
};

function timeAgo(dateStr) {
    const diff = Date.now() - new Date(dateStr).getTime();
    const d = Math.floor(diff / 86400000);
    if (d === 0) return 'сегодня';
    if (d === 1) return 'вчера';
    if (d < 30) return `${d}д. назад`;
    return new Date(dateStr).toLocaleDateString('ru-RU');
}

function Avatar({ user, size = 28 }) {
    return user?.avatar
        ? <img src={user.avatar} alt={user.username} style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover' }} />
        : <div className={styles.avatarFallback} style={{ width: size, height: size, fontSize: size * 0.4 }}>
            {(user?.username || '?')[0].toUpperCase()}
          </div>;
}

function IssueDetail({ repoId, issue, onBack, onStateChange, canModify }) {
    const { user } = useAuth();
    const [comments, setComments] = useState([]);
    const [newComment, setNewComment] = useState('');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        getIssueComments(repoId, issue.id).then(data => {
            setComments(data.results || data);
            setLoading(false);
        }).catch(() => setLoading(false));
    }, [repoId, issue.id]);

    async function submitComment(e) {
        e.preventDefault();
        if (!newComment.trim()) return;
        try {
            const comment = await addIssueComment(repoId, issue.id, { body: newComment });
            setComments(prev => [...prev, comment]);
            setNewComment('');
        } catch (err) { alert(err.message); }
    }

    async function toggleState() {
        try {
            if (issue.state === 'open') await closeIssue(repoId, issue.id);
            else await reopenIssue(repoId, issue.id);
            onStateChange();
        } catch (err) { alert(err.message); }
    }

    const type = TYPE_LABELS[issue.issue_type] || TYPE_LABELS.other;
    return (
        <div className={styles.detail}>
            <button className={styles.backBtn} onClick={onBack}>← Назад к задачам</button>
            <div className={styles.detailHeader}>
                <div className={styles.issueTitleRow}>
                    <span className={`${styles.stateTag} ${issue.state === 'open' ? styles.open : styles.closed}`}>
                        {issue.state === 'open' ? '● Открыт' : '✓ Закрыт'}
                    </span>
                    <h2 className={styles.issueTitle}>{issue.title}
                        <span className={styles.issueNumber}> #{issue.number}</span>
                    </h2>
                </div>
                <div className={styles.issueMeta}>
                    <Avatar user={issue.author} />
                    <span><strong>{issue.author?.username}</strong> открыл {timeAgo(issue.created_at)}</span>
                    <span className={styles.typeTag} style={{ borderColor: type.color + '44', color: type.color, background: type.color + '18' }}>
                        {type.label}
                    </span>
                    {issue.labels?.map((l, i) => (
                        <span key={i} className={styles.label}>{l}</span>
                    ))}
                </div>
            </div>

            {/* Original body */}
            {issue.body && (
                <div className={styles.commentCard}>
                    <div className={styles.commentHeader}>
                        <Avatar user={issue.author} />
                        <strong>{issue.author?.username}</strong>
                        <span className={styles.commentDate}>{timeAgo(issue.created_at)}</span>
                        <span className={styles.commentOriginal}>автор</span>
                    </div>
                    <div className={styles.commentBody}>{issue.body}</div>
                </div>
            )}

            {/* Comments */}
            {loading
                ? <div className={styles.loading}>Загрузка комментариев...</div>
                : comments.map(c => (
                    <div key={c.id} className={styles.commentCard}>
                        <div className={styles.commentHeader}>
                            <Avatar user={c.author} />
                            <strong>{c.author?.username}</strong>
                            <span className={styles.commentDate}>{timeAgo(c.created_at)}</span>
                            {c.is_edited && <span className={styles.editedTag}>изменён</span>}
                        </div>
                        <div className={styles.commentBody}>{c.body}</div>
                    </div>
                ))
            }

            {/* Reply / Actions */}
            {user ? (
                <form className={styles.commentForm} onSubmit={submitComment}>
                    <Avatar user={user} />
                    <div className={styles.commentInput}>
                        <textarea
                            placeholder="Оставить комментарий..."
                            value={newComment}
                            onChange={e => setNewComment(e.target.value)}
                            rows={3}
                        />
                        <div className={styles.commentActions}>
                            {canModify && (
                                <button type="button" className={styles.stateToggleBtn} onClick={toggleState}>
                                    {issue.state === 'open' ? '✓ Закрыть задачу' : '↺ Переоткрыть задачу'}
                                </button>
                            )}
                            <button type="submit" className={styles.submitBtn} disabled={!newComment.trim()}>
                                Отправить
                            </button>
                        </div>
                    </div>
                </form>
            ) : (
                <div className={styles.loginPrompt}>Войдите чтобы оставлять комментарии</div>
            )}
        </div>
    );
}

export default function IssuesTab({ repo, isOwner }) {
    const { user } = useAuth();
    const [issues, setIssues] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stateFilter, setStateFilter] = useState('open');
    const [typeFilter, setTypeFilter] = useState('');
    const [search, setSearch] = useState('');
    const [selectedIssue, setSelectedIssue] = useState(null);
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState({ title: '', body: '', issue_type: 'other', labels: '' });
    const [pagination, setPagination] = useState({});

    useEffect(() => { loadIssues(); }, [repo.id, stateFilter, typeFilter]);

    async function loadIssues() {
        setLoading(true);
        try {
            const params = { state: stateFilter };
            if (typeFilter) params.type = typeFilter;
            if (search) params.search = search;
            const data = await listIssues(repo.id, params);
            setIssues(data.results || data);
            setPagination({ count: data.count, total_pages: data.total_pages });
        } catch { setIssues([]); }
        setLoading(false);
    }

    async function submitIssue(e) {
        e.preventDefault();
        try {
            const payload = { ...form, labels: form.labels ? form.labels.split(',').map(s => s.trim()) : [] };
            await createIssue(repo.id, payload);
            setCreating(false);
            setForm({ title: '', body: '', issue_type: 'other', labels: '' });
            loadIssues();
        } catch (err) { alert(err.message); }
    }

    if (selectedIssue) {
        return (
            <IssueDetail
                repoId={repo.id}
                issue={selectedIssue}
                onBack={() => { setSelectedIssue(null); loadIssues(); }}
                onStateChange={() => {
                    setSelectedIssue(prev => ({ ...prev, state: prev.state === 'open' ? 'closed' : 'open' }));
                    loadIssues();
                }}
                canModify={isOwner || user?.username === selectedIssue.author?.username}
            />
        );
    }

    return (
        <div className={styles.wrap}>
            {/* Header */}
            <div className={styles.header}>
                <div className={styles.filters}>
                    <input
                        className={styles.searchInput}
                        placeholder="Поиск задач..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && loadIssues()}
                    />
                    <div className={styles.stateToggle}>
                        <button className={stateFilter === 'open' ? styles.activeFilter : styles.filterBtn} onClick={() => setStateFilter('open')}>
                            ● Открытые
                        </button>
                        <button className={stateFilter === 'closed' ? styles.activeFilter : styles.filterBtn} onClick={() => setStateFilter('closed')}>
                            ✓ Закрытые
                        </button>
                    </div>
                    <select className={styles.select} value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
                        <option value="">Все типы</option>
                        {Object.entries(TYPE_LABELS).map(([k, v]) => (
                            <option key={k} value={k}>{v.label}</option>
                        ))}
                    </select>
                </div>
                {user && (
                    <button className={styles.newBtn} onClick={() => setCreating(true)}>
                        + Новая задача
                    </button>
                )}
            </div>

            {/* Create form */}
            {creating && (
                <form className={styles.createForm} onSubmit={submitIssue}>
                    <h3>Создать задачу</h3>
                    <input
                        className={styles.inputField}
                        placeholder="Заголовок *"
                        value={form.title}
                        onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                        required
                    />
                    <textarea
                        className={styles.textareaField}
                        placeholder="Описание (необязательно)..."
                        value={form.body}
                        onChange={e => setForm(p => ({ ...p, body: e.target.value }))}
                        rows={5}
                    />
                    <div className={styles.formRow}>
                        <select className={styles.select} value={form.issue_type} onChange={e => setForm(p => ({ ...p, issue_type: e.target.value }))}>
                            {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                        </select>
                        <input
                            className={styles.inputField}
                            placeholder="Метки через запятую"
                            value={form.labels}
                            onChange={e => setForm(p => ({ ...p, labels: e.target.value }))}
                        />
                    </div>
                    <div className={styles.formActions}>
                        <button type="button" className={styles.cancelBtn} onClick={() => setCreating(false)}>Отмена</button>
                        <button type="submit" className={styles.submitBtn}>Создать задачу</button>
                    </div>
                </form>
            )}

            {/* Issues list */}
            <div className={styles.list}>
                {loading ? (
                    <div className={styles.loading}>Загрузка...</div>
                ) : issues.length === 0 ? (
                    <div className={styles.empty}>
                        <span style={{ fontSize: 40 }}>📋</span>
                        <p>{stateFilter === 'open' ? 'Нет открытых задач' : 'Нет закрытых задач'}</p>
                    </div>
                ) : (
                    issues.map(issue => {
                        const type = TYPE_LABELS[issue.issue_type] || TYPE_LABELS.other;
                        return (
                            <div key={issue.id} className={styles.issueRow} onClick={() => setSelectedIssue(issue)}>
                                <div className={styles.issueLeft}>
                                    <span className={`${styles.stateIcon} ${issue.state === 'open' ? styles.openIcon : styles.closedIcon}`}>
                                        {issue.state === 'open' ? '●' : '✓'}
                                    </span>
                                    <div className={styles.issueInfo}>
                                        <div className={styles.issueTitleLine}>
                                            <span className={styles.issueRowTitle}>{issue.title}</span>
                                            <span className={styles.typeTag} style={{ borderColor: type.color + '44', color: type.color, background: type.color + '18' }}>
                                                {type.label}
                                            </span>
                                            {issue.labels?.map((l, i) => <span key={i} className={styles.label}>{l}</span>)}
                                        </div>
                                        <div className={styles.issueMeta2}>
                                            #{issue.number} открыт {timeAgo(issue.created_at)} пользователем {issue.author?.username}
                                        </div>
                                    </div>
                                </div>
                                <div className={styles.issueRight}>
                                    {issue.comments_count > 0 && (
                                        <span className={styles.commentCount}>
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
                                            </svg>
                                            {issue.comments_count}
                                        </span>
                                    )}
                                    {issue.assignee && <Avatar user={issue.assignee} size={22} />}
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
}
